import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import AutosizeInput from 'react-input-autosize'
import { desktop, mobile, mobileLandscape, VIEWPORT_DESKTOP, VIEWPORT_MOBILE } from '../../styles/media'
import Tooltip, { StyledTooltip } from '../../components/general/Tooltip'
import Icon from '../../components/general/Icon'
import Checkbox from '../../components/form/Checkbox'
import themeLight from '../../styles/theme-light'
import Link, { StyledLink } from '../../components/router/Link'
import SaveButton from './components/SaveButton'
import { StyledBooleanValue } from '../../components/admin/BooleanValue'
import CoverImage, { EditIndicator, StyledCoverImage } from './components/CoverImage'
import DropDown from '../../components/button/DropDown'
import { slugifyUsername } from '../../helper/string'
import ProTag from '../../components/general/ProTag'
import { default as StyledTag } from '../../components/general/TagOld'
import DeckLinks from '../../layout/MoreMenu/DeckLinks'
import RequestDelayer from '../../helper/RequestDelayer'
import formats, { GENESYS, MASTER_DUEL } from '../../data/formats'
import FormatName, { StyledFormatName } from '../../components/ygo/FormatName'
import FormatSeasonSelect from '../../components/deck/FormatSeasonSelect'
import CoverImageSelect, { CoverImageList } from './components/CoverImageSelect'
import { time } from '../../helper/date'
import RelativeTime from '../../components/deck/RelativeTime'
import MasterDuelCosts from './components/MasterDuelCosts'
import StatsCollectionStatus, { Total } from './components/StatsCollectionStatus'
import FormatLink from './components/FormatLink'
import StatsLegalStatus from './components/StatsLegalStatus'
import history from '../../helper/history'
import SeasonName from '../../components/ygo/SeasonName'
import ThickHeading from '../../components/text/ThickHeading'
import StatsDeckPrice from './components/StatsDeckPrice'
import Avatar from '../../components/user/Avatar'
import { CARD_MARKET } from '../../data/priceSources'
import { PageNavigation } from '../../components/nav/PageNavigation'
import ShowOwnershipToggle from '../../components/collection/ShowOwnershipToggle'
import BetterButton from '../../components/button/BetterButton'
import BuyLinks, { BuyLinksSeparator, StyledBuyLinks } from '../card/BuyLinks'
import DeckTags, { Tag } from './components/DeckTags'
import Select from '../../components/form/Select'
import InlineField from '../../components/form/InlineField'

const Toolbar = ({ addMode, authToken, autoSort, canEdit, canEditNotArchived, cardSearchCollapsed, changeFormat, changeSeason, changeView, commentsCount, deck, deckCards, folders, forkDeckSafely, location, mode, ownsDeck, pricesLoading, saveDeck, setAutoSort, setDeck, settings, setMode, sortCards, subscribed, t, transparent, toggleDeckPriceInfo, toggleLegalInfo, toggleCollectionStatusInfo, updateUserSetting, view, viewport }) => {
	const folder = folders?.find(folder => folder.id === deck.folder)

	// deck counts
	const mainCount = deck.saved ? deck.main : deck.cards.filter(deckCard => deckCard.place === 'main').length
	const extraCount = deck.saved ? deck.extra : deck.cards.filter(deckCard => deckCard.place === 'extra').length
	const sideCount = deck.saved ? deck.side : deck.cards.filter(deckCard => deckCard.place === 'side').length
	const provision = deck.format === GENESYS ? deck.cards.filter(deckCard => deckCard.place !== 'note').reduce((acc, deckCard) => acc + (deckCard.card.provision ? deckCard.card.provision[deck.season?.id] || 0 : 0), 0) : null

	// deck is not updated (legal info) if not passed explicitly as a parameter
	const handleNameChange = (deck, name) => {
		setDeck({
			...deck,
			name
		}, 'name')
	}

	const [name, setName] = useState(deck.name)
	const [changeNameDelayer] = useState(new RequestDelayer(handleNameChange, 400))

	const nameRef = useRef(undefined)

	useEffect(() => {
		if (viewport === VIEWPORT_DESKTOP && deck.name === t('general.unnamedDeck')) {
			nameRef.current.select()
		}

		setName(deck.name)
	}, [deck.name, deck.slug])

	const saveToPublish = deck.saved && !deck.isPublic && deck.notPublicReasons && deck.notPublicReasons.length === 0
	const saveDisabled = (deck.saved || deck.saving) && !saveToPublish

	const [showOwnership, setShowOwnership] = useState(settings['collection.showOwnership'] !== false)
	const selectColor = subscribed ? settings['app.deckBuilderSelectColor'] : null

	const priceSource = Number(settings['app.priceSource']) || CARD_MARKET

	useEffect(() => {
		setShowOwnership(settings['collection.showOwnership'] !== false)
	}, [settings['collection.showOwnership']])

	const right = (
		<Right canEdit={canEdit}>
			{canEdit && (
				<>
					<EditItem>
						<Tooltip title={deck.saved && !saveToPublish ? time(deck.updatedAt) : null}>
							<SaveButton
								disabled={saveDisabled}
								saved={deck.saved && !saveToPublish}
								onClick={!saveDisabled ? () => saveDeck(t) : undefined}
							>
								{deck.saving && t('general.saving')}
								{!deck.saving && !saveDisabled && !saveToPublish && t('general.save')}
								{!deck.saving && !saveDisabled && saveToPublish && t('general.publish')}
								{!deck.saving && saveDisabled && t('general.saved')}
								{deck.saved && !saveToPublish && <small><RelativeTime date={deck.updatedAt} /></small>}
							</SaveButton>
						</Tooltip>
					</EditItem>
				</>
			)}

			{ownsDeck && !canEdit && (
				<EditItem>
					<BetterButton
						small
						to={`/deck-builder/${deck.slug}`}
						color={themeLight.primary}
					>
						<Icon name="pencil" />
						{t('general.edit')}
					</BetterButton>
				</EditItem>
			)}

			{ownsDeck && canEdit && (
				<EditItem>
					<Tooltip title={t('general.preview')}>
						<DropDownEditButton onClick={() => history.push(`/deck/${deck.slug}`)}><Icon name="desktop-computer" /></DropDownEditButton>
					</Tooltip>
				</EditItem>
			)}

			{!ownsDeck && (
				<EditItem>
					<BuyLinks deck deckCards={deckCards} />
					{/*<DeckBuyLinks deckCards={deckCards.filter(item => item.place !== 'note')} />*/}
					{/*<ActionButton
						to={!authToken ? '/signup?ref=userFeature' : null}
						onClick={() => forkDeckSafely(deck.slug, location, t)}
						color={themeLight.primary}
						icon="duplicate"
					>
						{t('general.import')}
					</ActionButton>*/}
				</EditItem>
			)}

			{viewport === VIEWPORT_DESKTOP && (
				<EditItem>
					<DropDown
						button={<DropDownEditButton><Icon name="chevron-down" /></DropDownEditButton>}
						right={0}
						top={40}
						width={267}
					>
						{onBeforeNavigate => <DeckLinks onBeforeNavigate={onBeforeNavigate} />}
					</DropDown>
				</EditItem>
			)}
		</Right>
	)

	const toolbarItems = (
		<>
			<EditItem>
				<Tooltip title={t('general.viewTile')}>
					<EditButton onClick={() => changeView('tile')} active={view === 'tile'}>
						<Icon name="view-grid" />
					</EditButton>
				</Tooltip>
				<Tooltip title={t('general.viewList')}>
					<EditButton onClick={() => changeView('list')} active={view === 'list'}>
						<Icon name="view-list" />
					</EditButton>
				</Tooltip>
				<Tooltip title={t('general.collection')}>
					<EditButton onClick={() => changeView('prices')} active={view === 'prices'}>
						<Icon name="archive" />
					</EditButton>
				</Tooltip>
				<Tooltip title={t('general.testDeck')}>
					<EditButton onClick={() => changeView('test')} active={view === 'test'}>
						<Icon name="beaker" />
					</EditButton>
				</Tooltip>
			</EditItem>
			{canEditNotArchived && (
				<>
					<EditItem>
						{viewport === VIEWPORT_MOBILE && (
							<Tooltip title={t('general.infoTool')}>
								<EditButton active={mode === 'info'} secondary onClick={() => setMode('info')}>
									<Icon name="information-circle" />
								</EditButton>
							</Tooltip>
						)}
						<Tooltip title={t('general.moveTool')}>
							<EditButton active={(viewport === VIEWPORT_DESKTOP && mode === 'info') || mode === 'move'} secondary={viewport === VIEWPORT_DESKTOP} onClick={() => setMode('move')}>
								<Icon name="hand" />
							</EditButton>
						</Tooltip>
						<Tooltip title={t('general.addTool')}>
							<EditButton active={mode === 'add'} green onClick={() => setMode('add')}>
								<Icon name="add-circle" />
							</EditButton>
						</Tooltip>
						<Tooltip title={t('general.removeTool')}>
							<EditButton active={mode === 'remove'} red onClick={() => setMode('remove')}>
								<Icon name="minus-circle" />
							</EditButton>
						</Tooltip>
					</EditItem>
				</>
			)}
			{canEdit && (
				<EditItem>
					<Tooltip title={t('general.showOwnership')}>
						<ShowOwnershipToggle deckbuilder />
					</Tooltip>
				</EditItem>
			)}
			{canEditNotArchived && (
				<>
					<EditItem>
						<Tooltip title={t('general.sort')}>
							<EditButton onClick={sortCards} active={autoSort}>
								<Icon name="sort-descending" />
							</EditButton>
						</Tooltip>
					</EditItem>
					<EditItem>
						<Tooltip title={t('general.autoSort')}>
							<Checkbox
								label="Auto"
								value={autoSort}
								onChange={setAutoSort}
							/>
						</Tooltip>
					</EditItem>
					<EditItem>&nbsp;</EditItem>
				</>
			)}
		</>
	)

	return (
		<>
			<StyledToolbar
				cardSearchCollapsed={cardSearchCollapsed}
				isEditingDeck={canEdit}
				transparent={transparent}
				addMode={addMode}
			>
				<Top isEditingDeck={canEdit}>
					{canEditNotArchived ? (
						<DropDown
							width={600}
							top={60}
							button={(
								<CoverImageButton subscribed={subscribed}>
									<CoverImage
										deck={deck}
										backgroundColor="#222"
										hideNotPlayable
										dark
										large
										dropdown
									/>
								</CoverImageButton>
							)}
							noStyles
						>
							{onBeforeNavigate => (
								<CoverImageSelectWrapper>
									{deckCards.length > 0 && (
										<ThickHeading>{t('general.selectCoverCard')}</ThickHeading>
									)}

									<CoverImageSelect
										deck={deck}
										onChange={(type, value) => {
											setDeck({
												...deck,
												coverCard: type === 'coverCard' ? value : null,
												coverArtwork: type === 'coverArtwork' ? value : null
											}, type)
											onBeforeNavigate()
										}}
										setDeck={setDeck}
										subscribed={subscribed}
										t={t}
									/>
								</CoverImageSelectWrapper>
							)}
						</DropDown>
					) : (
						<Tooltip title={deck.coverCard ? deck.coverCard.name : null}>
							<Link to={deck.coverCard ? `/card/${deck.coverCard.slug}` : null}>
								<CoverImage dark large deck={deck} backgroundColor="#222" />
							</Link>
						</Tooltip>
					)}

					<NameWrapper>
						{viewport === VIEWPORT_DESKTOP ? (
							<Name
								value={name}
								onChange={(event) => {
									const name = event.target.value

									setName(name)

									changeNameDelayer.delay(deck, name)
								}}
								disabled={!canEditNotArchived}
								color={selectColor}
								ref={nameRef}
							/>
						) : (
							<NameMobile to={canEdit ? `/deck-builder/${deck.slug}/properties` : undefined} as={canEdit ? Link : undefined}>{name}</NameMobile>
						)}

						<Format>
							{viewport === VIEWPORT_MOBILE ? (
								<>
									{deck.format && <FormatName format={deck.format} />}
									{deck.season && <>&nbsp;</>}
									<FormatLink to={`/format/${formats[deck.format].slug}${deck.season ? `/${deck.season.slug}` : ''}`}>
										{deck.season && <SeasonName season={deck.season} />}
										{deck.isCurrentSeason && (
											<Tooltip title={t('general.isCurrentSeason')}>
												<Icon name="badge-check" />
											</Tooltip>
										)}
									</FormatLink>
								</>
							) : (
								<FormatSeasonSelect
									disabled={!canEditNotArchived}
									format={deck.format}
									formatOnChange={format => changeFormat(Number(format))}
									season={deck.season}
									seasonOnChange={changeSeason}
									selectColor={selectColor}
								/>
							)}
						</Format>
					</NameWrapper>
				</Top>

				<Edit>
					<StatsWrapper>
						<Stats>
							{deck.format === GENESYS && (
								<StatsItem red={provision > 100}>
									<ProvisionWrapper red={provision > 100}>
										{provision > 100 && <Icon name="exclamation" />}
										{provision}<Total> / 100</Total>
									</ProvisionWrapper>
									<StatsLabel>{t('general.points')}</StatsLabel>
								</StatsItem>
							)}
							{(deck.format !== GENESYS || viewport === VIEWPORT_DESKTOP) && (
								<>
									<StatsItem empty={mainCount === 0}>
										{mainCount}
										<StatsLabel>Main</StatsLabel>
									</StatsItem>
									<StatsItem empty={extraCount === 0}>
										{extraCount}
										<StatsLabel>Extra</StatsLabel>
									</StatsItem>
									<StatsItem empty={sideCount === 0}>
										{sideCount}
										<StatsLabel>Side</StatsLabel>
									</StatsItem>
								</>
							)}

							<StatsLegalStatus
								deck={deck}
								toggleLegalInfo={toggleLegalInfo}
							/>
							<StatsCollectionStatus
								deckCards={deckCards}
								deckSlug={deck.slug}
								onClick={toggleCollectionStatusInfo}
								priceSource={priceSource}
							/>
							<StatsDeckPrice
								deckCards={deckCards}
								deckSlug={deck.slug}
								loading={pricesLoading}
								onClick={toggleDeckPriceInfo}
								priceSource={priceSource}
							/>
							{deck.format === MASTER_DUEL && viewport === VIEWPORT_DESKTOP && (
								<StatsItem>
									<MasterDuelCosts deck={deck} />
								</StatsItem>
							)}
						</Stats>

						{viewport === VIEWPORT_DESKTOP && (
							<FlyOut transparent={transparent}>
								{toolbarItems}
							</FlyOut>
						)}
					</StatsWrapper>
					{viewport === VIEWPORT_DESKTOP && right}
				</Edit>

				{viewport === VIEWPORT_MOBILE && (
					<>
						<Bottom>
							<div>
								{canEdit && !deck.hasResult ? (
									<>
										{t('general.saved')} <RelativeTime date={deck.updatedAt} />
										{folder && <>&nbsp;&middot;&nbsp;<DeckFolder><Icon name="folder" />{folder.name}</DeckFolder></>}
									</>
								) : (
									<>
										{deck.hasResult ? t('general.deckMetaSubmittedBy') : t('general.deckMetaCreatedBy')}
										&nbsp;&nbsp;
										<UserLink to={`/user/${slugifyUsername(deck.user.username)}`}>
											<Avatar width={40} avatar={deck.user.avatar} />
											<span>{deck.user.username}</span>{deck.user.subscribed === 'pro' && <ProTag dark title={deck.user.title} />}
										</UserLink>
										&nbsp;&nbsp;
										<RelativeTime date={deck.createdAt} />
									</>
								)}
							</div>
							{deck.tags && deck.tags.length > 0 && (
								<DeckTags format={deck.format} tags={deck.tags} />
							)}
							{deck.visibility === 'public' && (
								<div>
									<ScrollToCommentsLink onClick={() => document.getElementById('deck-comments').scrollIntoView()} unread={deck.unread} commentsCount={commentsCount}>
										<Icon name="annotation" />
										{commentsCount} {t('general.commentsCount', { count: commentsCount })}
									</ScrollToCommentsLink>
								</div>
							)}
							{deck.format === MASTER_DUEL && (
								<MasterDuelCosts deck={deck} />
							)}
						</Bottom>
						{canEdit && (
							<MobileButtons>
								<MobileButton to={`/deck-builder/${deck.slug}/properties`}>
									<Icon name="pencil" />
									<span>{t('general.properties')}</span>
								</MobileButton>
								<DropDown button={<MobileButton><Icon name="dots-horizontal" /></MobileButton>}>
									{onBeforeNavigate => deck ? (
										<DeckLinks onBeforeNavigate={onBeforeNavigate} />
									) : null}
								</DropDown>
							</MobileButtons>
						)}
					</>
				)}
			</StyledToolbar>
			{viewport === VIEWPORT_MOBILE && (
				<MobileToolbar
					canEdit
					addMode={addMode}
					transparent={transparent}
				>
					{toolbarItems}
					{!canEdit && right}
				</MobileToolbar>
			)}
		</>
	)
}

Toolbar.propTypes = {
	addMode: PropTypes.bool.isRequired,
	authToken: PropTypes.string,
	autoSort: PropTypes.bool.isRequired,
	canEdit: PropTypes.bool.isRequired,
	canEditNotArchived: PropTypes.bool.isRequired,
	cardSearchCollapsed: PropTypes.bool.isRequired,
	changeFormat: PropTypes.func.isRequired,
	changeSeason: PropTypes.func.isRequired,
	changeView: PropTypes.func.isRequired,
	commentsCount: PropTypes.number.isRequired,
	deck: PropTypes.object.isRequired,
	deckCards: PropTypes.array.isRequired,
	folders: PropTypes.array,
	forkDeckSafely: PropTypes.func.isRequired,
	location: PropTypes.object.isRequired,
	mode: PropTypes.string.isRequired,
	ownsDeck: PropTypes.bool.isRequired,
	pricesLoading: PropTypes.bool.isRequired,
	saveDeck: PropTypes.func.isRequired,
	setAutoSort: PropTypes.func.isRequired,
	setDeck: PropTypes.func.isRequired,
	settings: PropTypes.object.isRequired,
	setMode: PropTypes.func.isRequired,
	sortCards: PropTypes.func.isRequired,
	subscribed: PropTypes.string,
	t: PropTypes.func.isRequired,
	transparent: PropTypes.bool,
	toggleDeckPriceInfo: PropTypes.func.isRequired,
	toggleLegalInfo: PropTypes.func.isRequired,
	toggleCollectionStatusInfo: PropTypes.func.isRequired,
	updateUserSetting: PropTypes.func.isRequired,
	view: PropTypes.string.isRequired,
	viewport: PropTypes.string.isRequired
}

Toolbar.defaultProps = {
	authToken: null,
	folders: null,
	subscribed: null,
	transparent: true
}

const StyledToolbar = styled.div`
	background: ${props => props.theme.header};
	height: 80px;
	position: relative;
	transition: 0.2s;
	z-index: ${props => props.theme.zLayer4};

	${props => props.isEditingDeck && `
		position: sticky;
		top: 0;
	`}

	${props => props.transparent && `
		background: rgba(0, 0, 0, 0.7);

		${EditButton} {
			background: rgba(0, 0, 0, 0.4);
		}
	`}

	align-items: center;
	display: flex;
	padding: 0 2rem;

	${mobile`
		position: relative;
		left: 0;
		width: 100%;

		height: auto;
		flex-direction: column;
		justify-content: center;
		padding: 1rem;

		${props => props.addMode && `
			padding-top: calc(59px + 1rem);
		`}
	`}
`

const Top = styled.div`
	align-items: center;
	display: flex;
	justify-content: center;

	${props => !props.isEditingDeck && `
		margin: 0 auto;
		max-width: 1280px;
	`}

	${mobile`
		justify-content: flex-start;
		width: 90%;
	`}
`

const Edit = styled.div`
	align-items: center;
	display: flex;
	justify-content: center;
	flex: 1;

	${mobile`
		order: 2;
		align-self: flex-start;
		flex: 0 1 auto;
		margin-top: 1rem;

		background: rgba(0, 0, 0, 0.7);
		border-radius: 8px;
		padding: 0.25rem;
		width: 100%;
	`}
`

const Bottom = styled.div`
	align-items: flex-start;
	display: flex;
	flex-direction: column;

	color: #DDD;
	font-size: 0.8rem;
	font-weight: 500;
	margin-top: 0.25rem;
	white-space: nowrap;
	width: 100%;

	a {
		color: ${props => props.theme.white};
	}

	> div {
		margin-top: 0.5rem;
	}

	> div:first-child {
		white-space: normal;
	}

	${Tag} {
		color: ${props => props.theme.textInvertedLight};

		span {
			color: ${props => props.theme.white};
		}
	}

	${mobile`
		width: 90%;
	`}
`

const MobileButtons = styled.div`
	margin-top: 0.5rem;
	order: 2;
	width: 100%;

	align-items: center;
	display: flex;

	> * + * {
		margin-left: 1rem;
	}

	> :first-child {
		flex: 1;
		justify-content: center;
	}
`

const MobileButton = styled(Link)`
	background: rgba(0, 0, 0, 0.7);
	border-radius: 6px;
	color: #EEE;
	font-size: 0.9rem;
	font-weight: 600;
	padding: 0.5rem 0.5rem;
	text-decoration: none;
	transition: 0.1s;

	align-items: center;
	display: flex;

	svg {
		color: #CCC;
		height: 16px;
		width: 16px;
	}

	span {
		margin-left: 0.5rem;
	}

	&:active {
		background: rgba(0, 0, 0, 0.9);
	}
`

const CoverImageButton = styled(Link)`
	${desktop`
		&:hover {
			${props => props.subscribed ? 'img' : `${StyledCoverImage}`} {
				box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.5);
			}

			${StyledCoverImage} > svg {
				opacity: 0;
			}

			${EditIndicator}, ${EditIndicator} > svg {
				opacity: 1;
			}
		}
	`}

	${props => props.focused && `
		${props.subscribed ? 'img' : `${StyledCoverImage}`} {
			box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.5);
		}

		${StyledCoverImage} > svg {
			opacity: 0;
		}

		${EditIndicator}, ${EditIndicator} > svg {
			opacity: 1;
		}
	`}
`

const NameWrapper = styled.div`
	align-items: flex-start;
	display: flex;
	flex-direction: column;

	margin: 0 0 0 1rem;
`

const Name = styled(AutosizeInput)`
	margin-bottom: -4px;

	> input {
		background: transparent;
		transition: border-color 0.1s;

		border: 1px solid transparent;
		border-radius: 4px;
		color: ${props => props.theme.white};
		font-size: 1.5rem;
		margin-left: calc(-2px - 0.25rem);
		padding: 0.2rem 0.25rem;
		position: relative;
		text-overflow: ellipsis;
		width: fit-content;
		max-width: 22vw;
		z-index: 5;

		&:not([disabled]):hover {
			border-color: rgba(255, 255, 255, 0.2);
		}

		&:not([disabled]):focus {
			border-color: ${props => props.color || props.theme.primary};
			box-shadow: 0 0 0 1px ${props => props.color || props.theme.primary};
		}
	}

	${mobile`
		> input {
			font-size: 1.3rem;
			max-width: 67vw;
		}
	`}
`

const NameMobile = styled.h1`
	color: #FFF;
	font-size: 1.3rem;
	margin-bottom: 0;
	padding: 0;
	line-height: 1.2;
	text-decoration: none;
`

const Format = styled.div`
	align-items: center;
	display: flex;

	${desktop`
		margin-left: -4px;
	`}

	${mobile`
		color: #DDD;
		font-size: 0.8rem;
		font-weight: 500;
		margin-top: 0.25rem;
		white-space: nowrap;

		a {
			color: #FFF;
		}
	`}
`

const UserLink = styled(Link)`
	align-items: center;
	display: inline-flex;
	vertical-align: middle;
	text-decoration: none;

	> span:first-of-type {
		margin-left: 0;
		text-decoration: underline;
		text-decoration-color: ${props => props.theme.primary};
	}

	img, svg {
		margin-right: 0.35rem;
	}

	img {
		border-radius: 10px;
		height: 20px;
		width: 20px;
	}

	${StyledTag} {
		box-shadow: none;
		font-size: 0.8em;
		margin-left: 0.25rem;
	}
`

const FlyOut = styled.div`
	background: ${props => props.theme.background};
	border-radius: 8px;
	box-shadow: 0 0 0 4px ${props => props.transparent ? 'rgba(0, 0, 0, 0.9)' : props.theme.background}, 0 0 3px 3px rgba(0, 0, 0, 0.5);
	display: flex;

	position: absolute;
	top: 55px;
	left: 50%;
	transform: translate(-50%);

	${props => props.transparent && `
		background: rgba(0, 0, 0, 0.9);
	`}
`

const Right = styled.div`
	align-items: center;
	display: flex;
	margin-left: auto;

	> :last-child {
		margin-right: 0;
	}

	${mobile`
		${props => props.canEdit && `
			position: absolute;
			top: 0;
			right: 0;
			z-index: ${props.theme.zLayer6};

			background: #2D2D2D;
			height: 44px;
		`}
	`}
`

export const EditButton = styled.button`
	background: rgba(255, 255, 255, 0.1);
	border: 0;
	color: ${props => props.theme.textVeryLight};
	padding: 0.5rem;
	transition: color 0.1s;

	align-items: center;
	display: inline-flex;
	justify-content: center;
	white-space: nowrap;

	${props => props.active && !props.green && !props.red && !props.secondary && `
		background: ${props.theme.primary} !important;
		color: ${props.theme.white} !important;
	`}

	${props => props.active && props.green && `
		background: ${props.theme.green} !important;
		color: ${props.theme.white} !important;
	`}

	${props => props.active && props.red && `
		background: ${props.theme.red} !important;
		color: ${props.theme.white} !important;
	`}

	${props => props.active && props.secondary && `
		background: ${props.theme.secondary} !important;
		color: ${props.theme.white} !important;
	`}

	span {
		font-size: 0.9rem;
		margin-left: 0.35rem;
	}

	${desktop`
		${props => !props.active ? `
			&:hover {
				color: ${props.theme.textLight};
			}
		` : ''}
	`}

	${mobile`
		background: ${props => props.theme.backgroundLight};
		color: ${props => props.theme.textSecondary};
	`}
`

const DropDownEditButton = styled(EditButton)`
	${desktop`
		${props => !props.focused ? `
			&:hover {
				background: rgba(255, 255, 255, 0.2);
				color: ${props.theme.white};
				cursor: pointer;
			}
		` : `
			&, &:hover {
				background: rgba(255, 255, 255, 0.2);
				color: ${props.theme.white};
				cursor: pointer;
			}
		`}
	`}
`

const EditItem = styled.div`
	display: flex;

	& + & {
		margin-left: 1rem;
	}

	> ${EditButton} {
		border-radius: 8px;
	}

	> :first-child ${EditButton} {
		border-top-left-radius: 8px;
		border-bottom-left-radius: 8px;
	}

	> :last-child ${EditButton} {
		border-top-right-radius: 8px;
		border-bottom-right-radius: 8px;
	}

	${StyledTooltip} {
		display: block;
	}

	${StyledLink} {
		text-decoration: none;
	}

	${BuyLinksSeparator} {
		display: none;
	}

	${StyledBuyLinks} a {
		height: 36px;
	}
`

const StatsWrapper = styled.div`
	display: flex;

	position: absolute;
	left: 50%;
	transform: translate(-50%);
	top: 6px;

	${desktop`
		margin-left: auto;
	`}

	${mobile`
		position: sticky;
		top: 44px;
		left: 0;
		transform: none;
    `}
`

const Stats = styled.div`
	display: flex;
`

export const LegalInfoFlyOut = styled.div`
	display: none;

	position: absolute;
	top: 44px;
	width: 400px;
	z-index: ${props => props.theme.zLayer5};

	max-height: calc(100vh - 49px);
	max-height: calc(var(--vh, 1vh) * 100 - 49px);
	overflow: auto;

	box-shadow: ${props => props.theme.shadowDropDown};
	color: ${props => props.theme.text};
	font-size: 1rem;
	font-weight: 400;
	text-align: left;
`

export const StatsLabel = styled.div`
	color: #CCC;
	font-size: 0.7rem;
	font-weight: 500;
	margin-top: auto;
	text-transform: uppercase;

	${mobile`
		font-size: 0.6rem;
	`}
`

export const StatsItem = styled.button`
	background: transparent;
	border: 0;
	border-radius: 4px;
	color: ${props => props.theme.white};

	font-size: 1.3rem;
	font-weight: bold;
	height: 47px;
	padding: 0.25rem 0.5rem;
	position: relative;

	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	${StyledBooleanValue} {
		height: 18px;
		width: 18px;
		position: relative;
		top: 2px;
	}

	> ${StyledFormatName} {
		font-size: 1rem;
		position: relative;
		top: 2px;
	}

	${props => props.empty && `
		color: rgba(255, 255, 255, 0.5);
	`}

	${props => props.loading && `
		color: rgba(255, 255, 255, 0.5);
	`}

	${desktop`
		${props => !!(props.onClick || props.hasHover) && `
			&:hover {
				background: rgba(0, 0, 0, 0.2);

				${LegalInfoFlyOut} {
					display: block;
				}
			}
		`}

		${props => props.deck && `
			min-width: 92px;
		`}
	`}

	${mobile`
		font-size: 1.2rem;
		height: 42px;
		padding: 0.15rem 0.35rem;

		${props => props.deck && `
			min-width: 80px;
		`}
	`}
`

export const ProvisionWrapper = styled.div`
	white-space: nowrap;

	svg {
		height: 16px;
		width: 16px;
		margin-right: 0.25rem;
		vertical-align: middle;
	}

	${props => props.red && `
		color: ${props.theme.red};
	`}
`

const MobileToolbar = styled.div`
	background: ${props => props.theme.background};
	box-shadow: 0 2px 1px -1px ${props => props.theme.shadowLight};
	display: flex;

	min-height: 52px;
	overflow: auto;
	padding: 0.5rem;

	${props => props.canEdit && `
		position: sticky;
		top: 0;
		z-index: ${props.theme.zLayer4};
	`}

	${props => props.addMode && `
		display: none;
	`}

	${PageNavigation} {
		padding: 0 0.5rem;
	}

	${mobileLandscape`
    	position: relative;
    `}
`

export const ScrollToCommentsLink = styled(Link)`
	align-items: center;
	display: flex;
	transition: 0.1s;

	svg {
		height: 16px;
		width: 16px;
		margin-right: 0.25rem;

		${desktop`
			color: ${props => props.theme.textLight};
		`}

		${props => props.unread && `
			color: ${props.theme.primary} !important;
		`}
	}

	${props => props.commentsCount === 0 && `
		opacity: 0.5;
	`}
`

const CoverImageSelectWrapper = styled.div`
	${CoverImageList} {
		padding: 0.5rem 1rem;
	}

	${ThickHeading} {
		padding: 0.5rem 1rem;

		a {
			margin-left: 0.5rem;
			padding: 0;
		}
	}
`

export const DeckFolder = styled.span`
	align-items: center;
	display: inline-flex;
	vertical-align: middle;

	font-size: 0.7rem;
	font-weight: 500;

	svg {
		height: 14px;
		width: 14px;
		margin-right: 0.25rem;
	}
`

export default Toolbar
